<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './widgets/conversation/MessagePreview.vue';
import InboxName from './widgets/InboxName.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import PriorityMark from './widgets/conversation/PriorityMark.vue';
import CardLabels from './widgets/conversation/conversationCardComponents/CardLabels.vue';
import OperationTimer from 'dashboard/components/ui/OperationTimer.vue';
import AgentModeConversationCardContextMenu from './AgentModeConversationCardContextMenu.vue';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);

const store = useStore();

const showContextMenu = ref(false);
const contextMenuPosition = ref({ x: null, y: null });

const chatMetadata = computed(() => props.chat.meta || {});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  return senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
});

const unreadCount = computed(() => props.chat.unread_count || 0);

const hasUnread = computed(() => unreadCount.value > 0);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const inboxesList = computed(() => store.getters['inboxes/getInboxes']);

const showInboxName = computed(() => inboxesList.value.length > 1);

const messagePreviewClass = computed(() => [
  hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
]);

// CardLabels
const showLabelsSection = computed(() => props.chat.labels?.length > 0);

// OperationTimer
const shouldShowTimer = computed(() => {
  return (
    inbox.value?.operation_timer_enabled &&
    props.chat.status === 'open' &&
    props.chat.assigned_at !== null
  );
});

const operationTimerConfig = computed(() => {
  if (
    inbox.value?.operation_timer_enabled &&
    inbox.value?.operation_timer_config
  ) {
    return inbox.value.operation_timer_config;
  }
  return null;
});

const handleClick = () => {
  emit('click', props.chat);
};

// Context Menu
const openContextMenu = e => {
  e.preventDefault();
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full px-2 cursor-pointer hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group rounded-lg"
    :class="{
      'bg-n-alpha-1 dark:bg-n-alpha-3': isActive,
    }"
    @click="handleClick"
    @contextmenu="openContextMenu"
  >
    <Avatar
      :name="currentContact.name"
      :src="currentContact.thumbnail"
      :size="32"
      :status="currentContact.availability_status"
      :class="showInboxName ? 'mt-8' : 'mt-4'"
      hide-offline-status
      rounded-full
    />
    <div class="px-0 py-3 flex-1 min-w-0">
      <div v-if="showInboxName" class="flex items-center min-w-0 gap-1 mx-2">
        <InboxName :inbox="inbox" class="flex-1 min-w-0" />
        <PriorityMark :priority="chat.priority" class="flex-shrink-0" />
      </div>
      <h4
        class="conversation--user text-sm my-0 mx-2 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 ltr:pr-16 rtl:pl-16 text-n-slate-12"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        {{ currentContact.name }}
      </h4>
      <MessagePreview
        v-if="lastMessageInChat"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="messagePreviewClass"
      />
      <p
        v-else
        class="text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t('CHAT_LIST.NO_MESSAGES') }}
        </span>
      </p>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      />
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showInboxName ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <OperationTimer
            v-if="shouldShowTimer"
            :assigned-at="chat.assigned_at"
            :waiting-since="chat.waiting_since"
            :timer-config="operationTimerConfig"
          />
          <TimeAgo
            v-else
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
          />
        </span>
        <span
          v-if="hasUnread"
          class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 ltr:ml-auto rtl:mr-auto mt-1 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
    </div>
    <AgentModeConversationCardContextMenu
      v-if="showContextMenu"
      :conversation-id="chat.id"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="closeContextMenu"
    />
  </div>
</template>
